/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, IButtonProps, PrimaryButton } from '@fluentui/react/lib/Button';
import { ContextualMenu } from '@fluentui/react/lib/ContextualMenu';
import { Dialog, DialogFooter, DialogType, IDialogContentStyleProps, IDialogContentStyles, IDialogProps } from '@fluentui/react/lib/Dialog';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { IProgressIndicatorProps, IProgressIndicatorStyles, ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import { Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer';
import { Stack, StackItem } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction, focusAsync, getId } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import * as React from 'react';
import { Root, createRoot } from 'react-dom/client';
import { defineMessages } from 'react-intl';
import { ShimmerFactory } from 'src/core/components/ShimmerFactory';
import { useDelayedEffect } from 'src/core/hooks/useDelayedEffect';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { globalMessages } from 'src/core/utils/globalMessages';

export type BasicModalProps<T> = Omit<IDialogProps, 'title'> & {
    /**
     * Identifier for the source of the activity for logging purposes.
     */
    activitySource?: string;

    /**
     * The projected elements passed from the consumer between
     * the dialog tags.
     */
    children?: React.ReactNode;

    /**
     * Disables the 'Ok' button that submits the modal if false.
     */
    valid?: boolean;

    /**
     * Disables both buttons in the modal if true. Also disables the
     * modal close button icon.
     */
    disabled?: boolean;

    /**
     * Displays a shimmer view if true.
     */
    isLoading?: boolean;

    /**
     * Replace the text in the done button if provided.
     */
    doneButtonText?: string;

    /**
     * Extra props passed to the done button.
     */
    doneButtonProps?: IButtonProps;

    /**
     * Replace the text in the cancel button if provided.
     */
    cancelButtonText?: string;

    /**
     * Determines whether to render the default footer or not.
     */
    shouldRenderFooter?: boolean;

    errorMessage?: string;

    warningMessage?: string;

    /**
     * An optional function to render a custom footer.
     */
    onRenderFooter?: () => React.ReactNode;

    /**
     * A callback for when the modal 'Done' button is clicked.
     */
    onSubmit?: (data?: T) => void;
};

const messages = defineMessages({
    doneButton: { id: 'core.basicModal.doneButton', defaultMessage: 'Done' },
    moveMenuItemText: { id: 'core.basicModal.moveMenuItemText', defaultMessage: 'Move' },
    closeMenuItemText: { id: 'core.basicModal.closeMenuItemText', defaultMessage: 'Close' },
    closeButtonAriaLabel: { id: 'core.basicModal.closeButtonAriaLabel', defaultMessage: 'Close' }
});

const dialogClassNames = classNamesFunction<IDialogContentStyleProps, IDialogContentStyles>();
const progressClassNames = classNamesFunction<IProgressIndicatorProps, IProgressIndicatorStyles>();

const modalTheme = getScopedTheme('BasicModal');
const { styleDiv } = getStylist('BasicModal');

const progressStyles = progressClassNames({ itemProgress: { paddingTop: modalTheme.progressBarPadding } });

const LoadingOverlayContainer = styleDiv('LoadingOverlay', theme => ({
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: theme.overlayColor,
    zIndex: theme.modalContentOverlay as Property.ZIndex,
    pointerEvents: 'none'
}));

/**
 * Represents a basic modal wrapper that should be used as
 * a base for most of the modals of the system.
 *
 * @param props The properties passed to the modal.
 */
export const BasicModal = (props: BasicModalProps<Object>) => {
    const {
        valid,
        hidden,
        disabled,
        onSubmit,
        children,
        onDismiss,
        errorMessage,
        warningMessage,
        minWidth = 600,
        onRenderFooter,
        activitySource,
        doneButtonText,
        doneButtonProps,
        cancelButtonText,
        isLoading = false,
        dialogContentProps = {},
        shouldRenderFooter = true,
        modalProps = { isBlocking: false }
    } = props;
    const { localize } = useLocalization();
    const modalClassName = React.useRef(getId());
    const lastFocusedElement = React.useRef<HTMLElement>();
    const modalContentContainer = React.useRef<HTMLElement>();
    const rootRef = React.useRef<Root>();

    const logger = getLogger();

    React.useEffect(() => setupLoadingOverlayContainer(hidden, modalClassName.current, modalContentContainer), [hidden]);
    React.useEffect(() => {
        if (!modalContentContainer.current) return;
        rootRef.current = rootRef.current ?? createRoot(modalContentContainer.current);
        renderLoadingOverlay(disabled, rootRef.current);
    }, [disabled, modalContentContainer.current]);
    React.useEffect(() => handleLoadingOverlayAccessibility(disabled, lastFocusedElement), [disabled]);

    useDelayedEffect(() => {
        if (!activitySource) {
            return;
        }

        if (!hidden) {
            logger.startEvent(activitySource);
        } else {
            logger.endEvent(activitySource, false);
        }
    }, [hidden, activitySource]);

    /**
     * Ensures that the modal content styles have their pointer events
     * completely disabled when the modal is in a disabled state.
     */
    const dialogStyles = dialogClassNames({
        innerContent: { maxHeight: modalTheme.defaultMaxHeight, overflowY: 'auto', height: '100%' },
        inner: { display: 'flex', flexDirection: 'column', overflowY: 'hidden' },
        content: {
            position: 'relative',
            pointerEvents: disabled ? 'none' : 'unset',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
        },
        subText: { marginBottom: modalTheme.subTextMargin, color: modalTheme.subTextColor }
    });

    const dismissModal = (): void => {
        if (!disabled) {
            onDismiss?.();
        }
    };

    if (disabled) {
        lastFocusedElement.current = document.activeElement as HTMLElement;
    }

    return (
        <Dialog
            {...props}
            onDismiss={dismissModal}
            maxWidth={modalTheme.modalMaxWidth}
            styles={mergeStyleSets({ main: { width: `${minWidth}px !important`, minWidth: 'unset !important' } }, props.styles)}
            dialogContentProps={{
                type: DialogType.close,
                className: modalClassName.current,
                closeButtonAriaLabel: localize(messages.closeButtonAriaLabel),
                title: isLoading ? (
                    <Shimmer key={getId()} width="50%" shimmerElements={[{ type: ShimmerElementType.line, height: 20 }]} />
                ) : (
                    dialogContentProps.title
                ),
                ...dialogContentProps,
                styles: mergeStyleSets(dialogStyles, dialogContentProps?.styles)
            }}
            modalProps={{
                isDarkOverlay: true,
                dragOptions: {
                    menu: ContextualMenu,
                    moveMenuItemText: localize(messages.moveMenuItemText),
                    closeMenuItemText: localize(messages.closeButtonAriaLabel)
                },
                ...modalProps
            }}>
            <StackItem>
                {isLoading ? (
                    <ShimmerFactory includeHeaderShimmer={false} shimmerCount={10} shimmerSpacing={10} />
                ) : (
                    <Stack tokens={{ childrenGap: 5 }}>
                        {errorMessage && <MessageBar messageBarType={MessageBarType.error}>{errorMessage}</MessageBar>}
                        {warningMessage && <MessageBar messageBarType={MessageBarType.warning}>{warningMessage}</MessageBar>}
                        {children}
                    </Stack>
                )}
            </StackItem>
            {shouldRenderFooter && (
                <DialogFooter>
                    {onRenderFooter
                        ? onRenderFooter()
                        : [
                              <PrimaryButton
                                  {...doneButtonProps}
                                  onClick={onSubmit}
                                  key="basicModalDoneBtnId"
                                  disabled={disabled || !valid}
                                  data-automation-id="basicModalDoneBtnId"
                                  text={doneButtonText || localize(messages.doneButton)}
                              />,
                              <DefaultButton
                                  disabled={disabled}
                                  onClick={dismissModal}
                                  key="basicModalCancelBtnId"
                                  data-automation-id="basicModalCancelBtnId"
                                  text={cancelButtonText || localize(globalMessages.cancel)}
                              />
                          ]}
                </DialogFooter>
            )}
        </Dialog>
    );
};

/**
 * This function sets up the basic modal to support display
 * a loading overlay.
 *
 * When the modal is in a disabled state, an overlay is displayed
 * over the modal content along with a indeterminate progress bar
 * to communicate the loading status with the user.
 *
 * Fabric however, does not expose HTML element that represents the
 * entirety of the modal container. A reference to that element is
 * needed to cleanly display the loading overlay with absolute
 * positioning. In order to fetch that reference, direct DOM
 * manipulation is required:
 *
 * - A random className is generated and attached to the content
 *   container element via the dialog props.
 *
 * - When the modal hidden state is changed to be false (the modal
 *   is displayed), this function finds the div with the generated
 *   className. The modal has to be in a non-hidden state for this
 *   function to fire to ensure that the modal content container
 *   element was added in the DOM first.
 *
 * - A new div element is attached as the first child to that dialog
 *   content container and its reference is stored. The reason we have
 *   to create a new intermediate div container is that the React render
 *   function replaces the contents of whatever renders in. If we don't
 *   create this intermediate div, the modal container content will be
 *   deleted thus removing all modal content.
 *
 * - If the overlay container reference was already created before, then
 *   there is no need to create it again. Thus this function is short
 *   circuited if the reference was defined.
 *
 * @param isModalHidden Whether the root modal is hidden or not. If the
 * model is hidden, this function is skipped.
 * @param modalContainerClassName The class name of the modal content
 * container that we will use to inject the loading overlay div in.
 * @param loadingOverlayContainerRef A mutable reference to the loading
 * overlay container. If this reference is already defined, this function
 * is skipped.
 */
const setupLoadingOverlayContainer = (
    isModalHidden: boolean,
    modalContainerClassName: string,
    loadingOverlayContainerRef: React.MutableRefObject<HTMLElement>
) => {
    if (isModalHidden) {
        return;
    }

    if (loadingOverlayContainerRef.current) {
        return;
    }

    requestAnimationFrame(() => {
        const modalContentContainer = document.getElementsByClassName(modalContainerClassName)[0];
        const overlayDomContainer = document.createElement('div');

        modalContentContainer?.insertBefore(overlayDomContainer, modalContentContainer.firstChild);
        loadingOverlayContainerRef.current = overlayDomContainer;
    });

    return () => loadingOverlayContainerRef.current && loadingOverlayContainerRef.current.remove();
};

/**
 * Renders the loading overlay itself including the progress indicator
 * when the modal is in a disabled state. If the loading overlay
 * container reference is still not defined, then this function
 * is shortcut.
 *
 * @param isModalDisabled Whether the modal is disabled or not.
 * @param overlayContainerRef The modal loading overlay container
 * reference.
 */
const renderLoadingOverlay = (isModalDisabled: boolean, root: Root) => {
    requestAnimationFrame(() => {
        const LoadingOverlay = (
            <LoadingOverlayContainer>
                <ProgressIndicator styles={progressStyles} />
            </LoadingOverlayContainer>
        );
        root.render(isModalDisabled ? LoadingOverlay : null);
    });
};

/**
 * When the modal is in a disabled state:
 *
 * - The current active element should be stored in a
 *   temporary variable `lastFocusedElement`.
 *
 * - The current active element should be blurred so the
 *   user has no control over any of the modal elements.
 *
 * When the modal is back in a non disabled state:
 *
 * - If the `lastFocusedElement` reference is defined, then
 *   focus onto it. If it was not defined, do nothing.
 *
 * Note that storing the active element into the
 * `lastFocusedElement` reference happens inside the render
 * function, not in the effect. The reason for that is this
 * ensures that the last active element is captured before
 * buttons that were focused are disabled. To explain:
 *
 * 1 - The user enters data in the modal.
 * 2 - The user focuses the Done button with the keyboard.
 * 3 - The user presses Enter, the modal starts a process.
 * 4 - The modal disabled flag is changed to true.
 * 5 - The modal re-renders, the Done button that was focused
 *     gets disabled and loses focus.
 * 6 - React effects are fired to display the loader and disable
 *     the click events.
 * 7 - The process finishes (success/failure) and the disabled
 *     flag is back to being false
 *
 * The `lastFocusedElement` must be set in the rendering of step
 * 5, to ensure the correct element is captured and stored.
 *
 * @param isModalDisabled A flag to indicate whether the modal is
 * disabled or not.
 * @param lastFocusedElement A reference to the last focused
 * element before the modal was disabled.
 */
const handleLoadingOverlayAccessibility = (isModalDisabled: boolean, lastFocusedElement: React.MutableRefObject<HTMLElement>) => {
    const currentActiveElement = document.activeElement as HTMLElement;

    if (!isModalDisabled && lastFocusedElement.current) {
        focusAsync(lastFocusedElement.current);
    } else if (isModalDisabled) {
        currentActiveElement.blur();
    }
};
